import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/auth/useAuth";

const STORAGE_KEY = "proschool-branch-id";

export type BranchSelection = "all" | string;

interface BranchContextValue {
  selectedBranchId: BranchSelection;
  setSelectedBranchId: (id: BranchSelection) => void;
  effectiveBranchId: string | undefined;
}

const BranchContext = createContext<BranchContextValue | undefined>(undefined);

function readStoredBranch(): BranchSelection {
  const value = localStorage.getItem(STORAGE_KEY);
  return value === "all" || !value ? "all" : value;
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const { user, hasPermission } = useAuth();
  const [selectedBranchId, setSelectedBranchIdState] = useState<BranchSelection>(readStoredBranch);

  const setSelectedBranchId = useCallback((id: BranchSelection) => {
    setSelectedBranchIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const effectiveBranchId = useMemo(() => {
    if (user?.branchId) return user.branchId;
    if (!hasPermission("branch.all")) return user?.branchId ?? undefined;
    return selectedBranchId === "all" ? undefined : selectedBranchId;
  }, [user, hasPermission, selectedBranchId]);

  const value = useMemo(
    () => ({ selectedBranchId, setSelectedBranchId, effectiveBranchId }),
    [selectedBranchId, setSelectedBranchId, effectiveBranchId],
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch(): BranchContextValue {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used within BranchProvider");
  return ctx;
}
